import { useCallback, useEffect, useRef, useState } from 'react';
import { NotificationResponseDto } from '../helper-types/dtos/notification/responses/notification-response-dto';
import { NOTIFICATION_SERVICE } from '../services/notification/notification-service';

const UNREAD_COUNT_POLL_INTERVAL_MS = 60 * 1000;

interface UseNotificationsResult {
    unreadCount: number;
    notifications: NotificationResponseDto[];
    loading: boolean;
    fetchNotifications: () => Promise<void>;
    markAsRead: (notificationId: number) => Promise<void>;
    markAllAsRead: () => Promise<void>;
}

/**
 * Polls only the unread count on an interval (cheap — a single integer). The
 * full notification list is fetched lazily, only when the caller opens the panel.
 */
export const useNotifications = (enabled: boolean): UseNotificationsResult => {
    const [unreadCount, setUnreadCount] = useState(0);
    const [notifications, setNotifications] = useState<NotificationResponseDto[]>([]);
    const [loading, setLoading] = useState(false);
    const enabledRef = useRef(enabled);
    enabledRef.current = enabled;

    const refreshUnreadCount = useCallback(async () => {
        if (!enabledRef.current) {
            return;
        }
        try {
            const response = await NOTIFICATION_SERVICE.getUnreadCountAsync();
            if (response.success) {
                setUnreadCount(response.data ?? 0);
            }
        } catch {
            // Polling failures are silent — the bell just keeps its last known count.
        }
    }, []);

    useEffect(() => {
        if (!enabled) {
            return;
        }

        void refreshUnreadCount();
        const interval = setInterval(() => void refreshUnreadCount(), UNREAD_COUNT_POLL_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [enabled, refreshUnreadCount]);

    const fetchNotifications = useCallback(async () => {
        setLoading(true);
        try {
            const response = await NOTIFICATION_SERVICE.getMyNotificationsAsync();
            if (response.success) {
                setNotifications(response.data ?? []);
            }
        } finally {
            setLoading(false);
        }
    }, []);

    const markAsRead = useCallback(async (notificationId: number) => {
        const response = await NOTIFICATION_SERVICE.markAsReadAsync(notificationId);
        if (!response.success) {
            return;
        }

        let wasUnread = false;
        setNotifications((prev) => prev.map((n) => {
            if (n.id !== notificationId) {
                return n;
            }
            wasUnread = !n.isRead;
            return { ...n, isRead: true };
        }));

        if (wasUnread) {
            setUnreadCount((prev) => Math.max(0, prev - 1));
        }
    }, []);

    const markAllAsRead = useCallback(async () => {
        const response = await NOTIFICATION_SERVICE.markAllAsReadAsync();
        if (response.success) {
            setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
            setUnreadCount(0);
        }
    }, []);

    return { unreadCount, notifications, loading, fetchNotifications, markAsRead, markAllAsRead };
};
